// SEQ-180 phase 2, task P12: the widgets of one dashboard, with create,
// update, reorder, resize and delete.
//
// SAVES ARE PER ACTION, NOT A BATCH (plan section 7.1): adding a widget
// writes one row, a resize writes one row, a reorder writes only the rows
// whose position actually changed. Local state moves first so the grid does
// not jump, and a failed write puts the previous state back AND says why,
// which is the one place a revert is allowed: never silently.
//
// `layout` lives in its own column, not in `config`. `config.layout` is read
// only for the code-defined default dashboard (plan decision D11), which has
// no rows to carry a column; every persisted widget reads and writes the
// column, so there is one source of truth per widget (D18).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../components/ui/ToastModal";
import type { PnlWidgetConfig, PnlWidgetLayout } from "../../lib/pnl/pnlWidgetSchema";
import { checkReorderCount } from "./widgetReorder";

export const DEFAULT_WIDGET_LAYOUT: PnlWidgetLayout = { w: 2, h: 2 };

export interface PnlDashboardWidgetRow {
  /** The database row id. Distinct from `config.widgetId`, which is unique within a dashboard, not globally. */
  id: string;
  dashboardId: string;
  position: number;
  config: PnlWidgetConfig;
  layout: PnlWidgetLayout;
}

interface RawWidgetRow {
  id: string;
  dashboard_id: string;
  position: number;
  config: unknown;
  layout: unknown;
}

const WIDGET_COLUMNS = "id, dashboard_id, position, config, layout";

function decodeLayout(value: unknown): PnlWidgetLayout {
  if (typeof value !== "object" || value === null) return DEFAULT_WIDGET_LAYOUT;
  const l = value as Record<string, unknown>;
  const w = typeof l.w === "number" && l.w >= 1 && l.w <= 4 ? (l.w as PnlWidgetLayout["w"]) : DEFAULT_WIDGET_LAYOUT.w;
  const h = typeof l.h === "number" && l.h >= 1 && l.h <= 3 ? (l.h as PnlWidgetLayout["h"]) : DEFAULT_WIDGET_LAYOUT.h;
  return { w, h };
}

function decodeWidget(row: RawWidgetRow): PnlDashboardWidgetRow | null {
  // A saved config is dropped ONLY when it is unrenderable: the four things
  // the SQL CHECK asserts (D13) are what the renderer keys on, and without
  // them there is nothing to draw or to open in the editor.
  //
  // Everything else `validateWidgetConfig` catches is deliberately KEPT and
  // rendered. Those findings carry a section 7.3 message and a field, which
  // means the user can fix them in the drawer; dropping the widget instead
  // would leave them with a dashboard that silently lost a card and no way
  // to get it back. A blank name or a comparison on a lifetime range is a
  // thing to correct, not a reason to hide the widget.
  const config = row.config;
  if (typeof config !== "object" || config === null) return null;
  const shape = config as Record<string, unknown>;
  const renderable =
    shape.schemaVersion === 1 &&
    typeof shape.widgetId === "string" &&
    shape.widgetId.length > 0 &&
    typeof shape.chartType === "string" &&
    typeof shape.metric === "string";
  if (!renderable) return null;

  return {
    id: row.id,
    dashboardId: row.dashboard_id,
    position: row.position,
    config: config as PnlWidgetConfig,
    layout: decodeLayout(row.layout),
  };
}

export interface UsePnlDashboardWidgetsResult {
  widgets: PnlDashboardWidgetRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  addWidget: (config: PnlWidgetConfig, layout: PnlWidgetLayout) => Promise<boolean>;
  updateWidget: (id: string, config: PnlWidgetConfig) => Promise<boolean>;
  resizeWidget: (id: string, layout: PnlWidgetLayout) => Promise<boolean>;
  reorderWidgets: (orderedIds: string[]) => Promise<boolean>;
  deleteWidget: (id: string) => Promise<boolean>;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- pnl_dashboard_widgets is not in the generated Supabase types yet (SEQ-180 phase 2) */
export function usePnlDashboardWidgets(dashboardId: string | null): UsePnlDashboardWidgetsResult {
  const { addToast } = useToast();
  const [widgets, setWidgets] = useState<PnlDashboardWidgetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!dashboardId) {
      setWidgets([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (supabase as any)
      .from("pnl_dashboard_widgets")
      .select(WIDGET_COLUMNS)
      .eq("dashboard_id", dashboardId)
      .order("position", { ascending: true })
      .then(({ data, error: readError }: { data: RawWidgetRow[] | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (readError) {
          setError(readError.message);
          setWidgets([]);
        } else {
          setError(null);
          const decoded = (data ?? []).map(decodeWidget).filter((w): w is PnlDashboardWidgetRow => w !== null);
          const dropped = (data ?? []).length - decoded.length;
          if (dropped > 0) {
            addToast({
              type: "warning",
              title: `${dropped} widget${dropped === 1 ? "" : "s"} could not be read`,
              message: "Their saved configuration has no chart type or metric, so there is nothing to draw. Delete them to clear this.",
            });
          }
          setWidgets(decoded);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addToast is stable from context; re-read only on dashboard change or refresh
  }, [dashboardId, refreshToken]);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  const addWidget = useCallback(
    async (config: PnlWidgetConfig, layout: PnlWidgetLayout): Promise<boolean> => {
      if (!dashboardId) return false;
      const position = widgets.reduce((max, w) => Math.max(max, w.position), -1) + 1;

      const { data, error: insertError } = await (supabase as any)
        .from("pnl_dashboard_widgets")
        .insert({ dashboard_id: dashboardId, position, config, layout })
        .select(WIDGET_COLUMNS)
        .single();

      if (insertError || !data) {
        addToast({
          type: "error",
          title: "Could not add the widget",
          message: insertError?.message ?? "The database returned no row.",
        });
        return false;
      }

      const decoded = decodeWidget(data as RawWidgetRow);
      if (decoded) setWidgets((prev) => [...prev, decoded]);
      return true;
    },
    [dashboardId, widgets, addToast],
  );

  const updateWidget = useCallback(
    async (id: string, config: PnlWidgetConfig): Promise<boolean> => {
      const previous = widgets;
      setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, config } : w)));

      const { data, error: updateError } = await (supabase as any)
        .from("pnl_dashboard_widgets")
        .update({ config, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id");

      if (updateError || !Array.isArray(data) || data.length === 0) {
        setWidgets(previous);
        addToast({
          type: "error",
          title: "Could not save the widget",
          message: updateError?.message ?? "The dashboard is not one you can edit.",
        });
        return false;
      }
      return true;
    },
    [widgets, addToast],
  );

  const resizeWidget = useCallback(
    async (id: string, layout: PnlWidgetLayout): Promise<boolean> => {
      const previous = widgets;
      setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, layout } : w)));

      const { data, error: updateError } = await (supabase as any)
        .from("pnl_dashboard_widgets")
        .update({ layout, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select("id");

      if (updateError || !Array.isArray(data) || data.length === 0) {
        setWidgets(previous);
        addToast({
          type: "error",
          title: "Could not resize the widget",
          message: updateError?.message ?? "The dashboard is not one you can edit.",
        });
        return false;
      }
      return true;
    },
    [widgets, addToast],
  );

  const reorderWidgets = useCallback(
    async (orderedIds: string[]): Promise<boolean> => {
      const previous = widgets;
      const byId = new Map(previous.map((w) => [w.id, w]));
      const next = orderedIds
        .map((id, index) => {
          const widget = byId.get(id);
          return widget ? { ...widget, position: index } : null;
        })
        .filter((w): w is PnlDashboardWidgetRow => w !== null);

      setWidgets(next);

      const changed = next.filter((w) => byId.get(w.id)?.position !== w.position);
      if (changed.length === 0) return true;

      // React re-review, MEDIUM. The previous fix batched the renumber into a
      // single upsert, which made it atomic but sent every widget's whole
      // `config` and `layout` along with its position. On a shared dashboard
      // that means a drag silently overwrites an edit someone else saved
      // between this client's last read and the drop: the reorder was never
      // asking to change a config, so it should not be sending one.
      //
      // `pnl_dashboard_widgets_reorder` writes POSITIONS ONLY, inside one
      // transaction, under RLS, and returns how many rows it renumbered.
      //
      // The count is checked, not just `{ error }`. A call can come back
      // clean and still have written fewer rows than asked (RLS matched some
      // and not others, or a widget was deleted between the drag and the
      // drop), and a count that does not match the ids sent is a failure
      // whichever way it misses. On any failure the optimistic order is
      // dropped and the widgets are re-fetched, because at that point the
      // client cannot vouch for what is on screen. The rules live in
      // widgetReorder.ts with seven Deno cases.
      const { data, error: rpcError } = await (supabase as any).rpc("pnl_dashboard_widgets_reorder", {
        p_dashboard_id: dashboardId,
        p_widget_ids: orderedIds,
      });

      if (rpcError) {
        // The function is transactional, so an error means nothing was
        // written and `previous` is still what the server holds.
        setWidgets(previous);
        addToast({ type: "error", title: "Could not save the new order", message: rpcError.message });
        return false;
      }

      const counted = checkReorderCount(data, orderedIds.length);
      if (!counted.ok) {
        addToast({ type: "error", title: "The new order was not saved", message: counted.message });
        refresh();
        return false;
      }

      return true;
    },
    [widgets, addToast, dashboardId, refresh],
  );

  const deleteWidget = useCallback(
    async (id: string): Promise<boolean> => {
      const previous = widgets;
      setWidgets((prev) => prev.filter((w) => w.id !== id));

      const { data, error: deleteError } = await (supabase as any)
        .from("pnl_dashboard_widgets")
        .delete()
        .eq("id", id)
        .select("id");

      if (deleteError || !Array.isArray(data) || data.length === 0) {
        setWidgets(previous);
        addToast({
          type: "error",
          title: "Could not delete the widget",
          message: deleteError?.message ?? "The dashboard is not one you can edit.",
        });
        return false;
      }
      return true;
    },
    [widgets, addToast],
  );

  return { widgets, loading, error, refresh, addWidget, updateWidget, resizeWidget, reorderWidgets, deleteWidget };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
